import { useState, useEffect } from 'react';
import {
  Loader2, CheckCircle2, AlertTriangle, HelpCircle, Ban,
  ChevronDown, ChevronRight, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { diagnosticsAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

const OUTCOME_STYLES = {
  verified: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-950/30', label: 'Verified' },
  no_checkout: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-950/30', label: 'No checkout' },
  ambiguous: { icon: HelpCircle, color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-950/30', label: 'Ambiguous' },
  non_turnover: { icon: Ban, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/30', label: 'Non-turnover' },
  unparseable: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-950/30', label: 'Unparseable' },
};

function SummaryCard({ label, value, colorClass }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>
      <p className={`text-lg font-semibold ${colorClass || ''}`}>{value}</p>
    </div>
  );
}

function OutcomeBadge({ outcome }) {
  const s = OUTCOME_STYLES[outcome] || { color: 'text-gray-500', label: outcome };
  return <span className={`text-xs font-medium ${s.color}`}>{s.label}</span>;
}

// ── Coverage gap view (default) ──────────────────────────────────────

function CoverageView() {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const loadCoverage = async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.cleaningVerificationCoverage();
      setCoverage(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCoverage(); }, []);

  const runReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await diagnosticsAPI.cleaningVerificationReconcile();
      toast.success(`Reconciled: ${data.recomputed} verified, ${data.errors?.length || 0} errors`);
      await loadCoverage();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReconciling(false);
    }
  };

  const entities = coverage?.by_entity ? Object.entries(coverage.by_entity) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button onClick={loadCoverage} disabled={loading} size="sm" variant="outline">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
        {coverage && coverage.total_gaps > 0 && (
          <Button onClick={runReconcile} disabled={reconciling} size="sm">
            {reconciling && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Verify all ({coverage.total_gaps} gaps)
          </Button>
        )}
      </div>

      {loading && !coverage && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading coverage...
        </div>
      )}

      {coverage && coverage.total_gaps === 0 && (
        <div className="flex items-center gap-2 text-sm text-green-600 py-2">
          <CheckCircle2 className="h-4 w-4" />
          All statement (entity, month) pairs have current cleaning verification results.
        </div>
      )}

      {coverage && coverage.total_gaps > 0 && (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {coverage.total_gaps} gap{coverage.total_gaps !== 1 ? 's' : ''} across {entities.length} entit{entities.length !== 1 ? 'ies' : 'y'}
          </div>

          {entities.sort((a, b) => a[0].localeCompare(b[0])).map(([eid, info]) => (
            <div key={eid} className="border rounded-md p-3">
              <div className="text-sm font-medium mb-2">
                {info.entity_name} <span className="text-muted-foreground font-normal">({eid})</span>
                <span className="ml-2 text-xs text-muted-foreground">{info.count} gap{info.count !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {info.months.map((m) => (
                  <span
                    key={m.month}
                    className={`text-xs px-2 py-0.5 rounded font-mono ${
                      m.reason === 'missing'
                        ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400'
                    }`}
                    title={m.reason === 'missing' ? 'No verification result' : 'Verification older than latest statement'}
                  >
                    {m.month} ({m.reason})
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Single entity+month view (targeted re-run) ──────────────────────

function SingleVerificationView() {
  const [entityId, setEntityId] = useState('eci');
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [lineTableOpen, setLineTableOpen] = useState(false);
  const [cleanTableOpen, setCleanTableOpen] = useState(false);
  const [dqOpen, setDqOpen] = useState(true);

  const runCheck = async (force = false) => {
    if (!entityId || !month) {
      toast.error('Entity and month are required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.cleaningVerification({ entity_id: entityId, month, force });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const s = result?.summary;

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Entity</label>
          <input
            type="text"
            className="block mt-1 border rounded px-2 py-1 text-sm w-32"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            placeholder="e.g. eci"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Month</label>
          <input
            type="text"
            className="block mt-1 border rounded px-2 py-1 text-sm w-32"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            placeholder="YYYY-MM"
          />
        </div>
        <Button onClick={() => runCheck(false)} disabled={loading} size="sm">
          {loading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
          Load
        </Button>
        <Button onClick={() => runCheck(true)} disabled={loading} size="sm" variant="outline">
          {loading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
          Recompute
        </Button>
      </div>

      {result && (
        <div className="space-y-6 mt-4">
          {/* Computed timestamp + agent coverage */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {result.computed_at && (
              <span>Computed: <span className="font-medium">{new Date(result.computed_at).toLocaleString()}</span></span>
            )}
            {result.agents?.length > 0 && (
              <span>Agent(s): <span className="font-medium">{result.agents.join(', ')}</span></span>
            )}
          </div>

          {/* Summary cards */}
          <div>
            <div className="text-sm font-semibold mb-2">Line classification</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <SummaryCard label="Total cleaning lines" value={s?.total_cleaning_lines ?? 0} />
              <SummaryCard label="Turnover clean" value={s?.turnover_clean ?? 0} colorClass="text-green-600" />
              <SummaryCard label="Non-turnover" value={s?.non_turnover ?? 0} colorClass="text-blue-500" />
              <SummaryCard label="Unparseable" value={s?.unparseable ?? 0} colorClass={s?.unparseable > 0 ? 'text-red-500' : ''} />
              <SummaryCard label="Bundled lines" value={s?.bundled_lines ?? 0} colorClass="text-orange-500" />
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold mb-2">Verification outcomes (per parsed clean)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryCard label="Total parsed cleans" value={s?.total_parsed_cleans ?? 0} />
              <SummaryCard label="Verified" value={s?.verified ?? 0} colorClass="text-green-600" />
              <SummaryCard label="No checkout" value={s?.no_checkout ?? 0} colorClass="text-yellow-600" />
              <SummaryCard label="Ambiguous" value={s?.ambiguous ?? 0} colorClass="text-orange-500" />
            </div>
          </div>

          {/* Data quality panel */}
          <div className="border rounded-md">
            <button
              onClick={() => setDqOpen(!dqOpen)}
              className="flex items-center gap-1 w-full px-3 py-1.5 text-xs font-medium text-left hover:bg-muted/50"
            >
              {dqOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Data quality ({(result.data_quality?.unparseable_lines?.length || 0) + (result.data_quality?.bundled_lines?.length || 0)} items)
            </button>
            {dqOpen && (
              <div className="border-t p-3 space-y-3">
                {/* Unparseable */}
                {result.data_quality?.unparseable_lines?.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-red-500 mb-1">
                      Unparseable lines ({result.data_quality.unparseable_lines.length})
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2 border">Property</th>
                            <th className="text-left p-2 border">Description</th>
                            <th className="text-right p-2 border">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.data_quality.unparseable_lines.map((l, i) => (
                            <tr key={i} className="border-t bg-red-50/50 dark:bg-red-950/20">
                              <td className="p-2 border font-mono text-xs">{l.property_name_raw || l.property_ref || '-'}</td>
                              <td className="p-2 border">{l.description || '(empty)'}</td>
                              <td className="p-2 border text-right font-mono">{l.amount || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {result.data_quality?.unparseable_lines?.length === 0 && (
                  <div className="text-xs text-green-600">No unparseable lines. Parser accuracy: 100%.</div>
                )}

                {/* Bundled */}
                {result.data_quality?.bundled_lines?.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-orange-500 mb-1">
                      Bundled lines ({result.data_quality.bundled_lines.length})
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2 border">Property</th>
                            <th className="text-left p-2 border">Description</th>
                            <th className="text-left p-2 border">Parsed dates</th>
                            <th className="text-right p-2 border">Amount (total)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.data_quality.bundled_lines.map((l, i) => (
                            <tr key={i} className="border-t bg-orange-50/50 dark:bg-orange-950/20">
                              <td className="p-2 border font-mono text-xs">{l.property_name_raw || l.property_ref || '-'}</td>
                              <td className="p-2 border">{l.description}</td>
                              <td className="p-2 border font-mono">{l.parsed_dates?.join(', ') || '-'}</td>
                              <td className="p-2 border text-right font-mono">{l.amount || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Per-line table (collapsible) */}
          <div className="border rounded-md">
            <button
              onClick={() => setLineTableOpen(!lineTableOpen)}
              className="flex items-center gap-1 w-full px-3 py-1.5 text-xs font-medium text-left hover:bg-muted/50"
            >
              {lineTableOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              All cleaning lines ({result.lines?.length || 0})
            </button>
            {lineTableOpen && (
              <div className="overflow-x-auto border-t max-h-[500px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-background">
                    <tr className="bg-muted/30 border-b">
                      <th className="px-2 py-1 text-left font-medium">Property</th>
                      <th className="px-2 py-1 text-left font-medium">Agent</th>
                      <th className="px-2 py-1 text-left font-medium">Description</th>
                      <th className="px-2 py-1 text-right font-medium">Amount</th>
                      <th className="px-2 py-1 text-left font-medium">Classification</th>
                      <th className="px-2 py-1 text-left font-medium">Parsed dates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.lines?.map((l, i) => {
                      const style = OUTCOME_STYLES[l.classification] || {};
                      return (
                        <tr key={i} className={`border-t ${style.bg || ''}`}>
                          <td className="px-2 py-1 font-mono text-xs whitespace-nowrap">
                            {l.property_name_raw || l.property_ref || '-'}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">{l.agency || '-'}</td>
                          <td className="px-2 py-1 max-w-xs truncate">{l.description || '-'}</td>
                          <td className="px-2 py-1 text-right font-mono whitespace-nowrap">{l.amount || '-'}</td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            <OutcomeBadge outcome={l.classification} />
                            {l.non_turnover_reason && (
                              <span className="ml-1 text-[10px] text-muted-foreground">({l.non_turnover_reason})</span>
                            )}
                          </td>
                          <td className="px-2 py-1 font-mono text-xs">{l.parsed_dates?.join(', ') || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Parsed cleans with match outcome (collapsible) */}
          <div className="border rounded-md">
            <button
              onClick={() => setCleanTableOpen(!cleanTableOpen)}
              className="flex items-center gap-1 w-full px-3 py-1.5 text-xs font-medium text-left hover:bg-muted/50"
            >
              {cleanTableOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Parsed cleans with match outcome ({result.parsed_cleans?.length || 0})
            </button>
            {cleanTableOpen && (
              <div className="overflow-x-auto border-t max-h-[600px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-background">
                    <tr className="bg-muted/30 border-b">
                      <th className="px-2 py-1 text-left font-medium">Property</th>
                      <th className="px-2 py-1 text-left font-medium">Clean date</th>
                      <th className="px-2 py-1 text-left font-medium">Amount</th>
                      <th className="px-2 py-1 text-left font-medium">Outcome</th>
                      <th className="px-2 py-1 text-left font-medium">Matched check-out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.parsed_cleans?.map((pc, i) => {
                      const style = OUTCOME_STYLES[pc.match_outcome] || {};
                      return (
                        <tr key={i} className={`border-t ${style.bg || ''}`}>
                          <td className="px-2 py-1 font-mono text-xs whitespace-nowrap">
                            {pc.property_name_raw || pc.property_id || '-'}
                          </td>
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{pc.clean_date}</td>
                          <td className="px-2 py-1 font-mono whitespace-nowrap">
                            {pc.is_bundled ? (
                              <span className="text-orange-500" title={pc.amount_note}>{pc.amount_note}</span>
                            ) : (
                              pc.source_amount || '-'
                            )}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            <OutcomeBadge outcome={pc.match_outcome} />
                          </td>
                          <td className="px-2 py-1">
                            {pc.match_outcome === 'verified' && pc.matched_checkouts?.[0] && (
                              <span className="font-mono text-green-700 dark:text-green-400">
                                {pc.matched_checkouts[0].check_out} ({pc.matched_checkouts[0].guest_name})
                              </span>
                            )}
                            {pc.match_outcome === 'ambiguous' && pc.matched_checkouts && (
                              <div className="space-y-0.5">
                                {pc.matched_checkouts.map((c, j) => (
                                  <div key={j} className="font-mono text-orange-600">
                                    {c.check_out} ({c.guest_name})
                                  </div>
                                ))}
                              </div>
                            )}
                            {pc.match_outcome === 'no_checkout' && (
                              <span className="text-muted-foreground">No check-out in window</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main panel ───────────────────────────────────────────────────────

export function CleaningVerificationPanel() {
  const [tab, setTab] = useState('coverage');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Cleaning Verification</CardTitle>
        <p className="text-sm text-muted-foreground">
          Coverage view shows all (entity, month) pairs with statements but missing or stale
          verification. Use "Verify all" to backfill gaps in one click. Per-entity view for
          targeted re-runs.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-1 border-b">
          <button
            onClick={() => setTab('coverage')}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${
              tab === 'coverage'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Coverage
          </button>
          <button
            onClick={() => setTab('single')}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${
              tab === 'single'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Per-entity
          </button>
        </div>

        {tab === 'coverage' && <CoverageView />}
        {tab === 'single' && <SingleVerificationView />}
      </CardContent>
    </Card>
  );
}
